// src/components/PatientDetails/PatientEnvironmentsModal.tsx
// -----------------------------------------------------------------------------
// Edit which PERSONAL environments a patient belongs to, from the patient-details
// header. Add the patient to, or remove it from, environments the current user
// manages. Institution/consortium environments are provisioned by the XXMM sync
// and are shown read-only (no remove control). The backend applies the change +
// cascades it to every patient-scoped record asynchronously; this modal polls
// the task and refreshes when it completes. See cursor_logs/035 §F.
// -----------------------------------------------------------------------------

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import { useSnackbar } from "notistack";
import {
  fetchData,
  createData,
  deleteDataWithBody,
} from "../../api/apiHelpers";
import { useTranslation } from "../../i18n";
import {
  athenaColors,
  athenaDarkPaperSx,
  athenaDarkTitleSx,
  athenaDarkContentSx,
  athenaDarkActionsSx,
  athenaDarkPrimaryButtonSx,
  athenaDarkSecondaryButtonSx,
} from "../layout/AthenaDialogStyles";

interface EnvItem {
  id: number;
  name: string;
  kind: string;
  controllable?: boolean;
}

interface EnvData {
  current: EnvItem[];
  addable: EnvItem[];
}

interface DispatchResp {
  status: string;
  task_id: string | null;
}

interface TaskStatus {
  status: "pending" | "processing" | "completed" | "error";
  error?: string;
}

export interface PatientEnvironmentsModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string | number;
  /** Called with the patient's new environment names so the header can update. */
  onChanged?: (names: string[]) => void;
}

const POLL_INTERVAL_MS = 1500;
const POLL_MAX_ATTEMPTS = 40; // ~60s

const PatientEnvironmentsModal: React.FC<PatientEnvironmentsModalProps> = ({
  open,
  onClose,
  patientId,
  onChanged,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState<EnvData>({ current: [], addable: [] });
  const [selectedAdd, setSelectedAdd] = useState<EnvItem[]>([]);

  // Keep onChanged in a ref so `load` stays referentially stable even if the
  // parent passes a fresh callback each render (avoids a re-fetch loop).
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetchData<EnvData>(
        `/patients/${patientId}/environments/`
      );
      setData(resp);
      onChangedRef.current?.(resp.current.map((e) => e.name));
    } catch {
      enqueueSnackbar(
        t("patient.env_load_error", "Could not load environments."),
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  }, [patientId, enqueueSnackbar, t]);

  useEffect(() => {
    if (open) {
      setSelectedAdd([]);
      load();
    }
  }, [open, load]);

  const pollTask = async (taskId: string): Promise<void> => {
    for (let i = 0; i < POLL_MAX_ATTEMPTS; i++) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      const s = await fetchData<TaskStatus>(
        `/patients/environment-task/${taskId}/`
      );
      if (s.status === "completed") return;
      if (s.status === "error") {
        throw new Error(s.error || "failed");
      }
    }
    throw new Error("timeout");
  };

  const reportError = (e: unknown) => {
    const msg = e instanceof Error ? e.message : "";
    enqueueSnackbar(
      msg === "cannot_remove_last_environment"
        ? t(
            "patient.env_last_error",
            "A patient must remain in at least one environment."
          )
        : t("patient.env_update_error", "Could not update environments."),
      { variant: "error", autoHideDuration: 5000 }
    );
  };

  const handleAdd = async () => {
    if (selectedAdd.length === 0) return;
    const toAdd = selectedAdd;
    setSelectedAdd([]);
    setBusy(true);
    try {
      for (const env of toAdd) {
        const resp = await createData<{ environment_id: number }, DispatchResp>(
          `/patients/${patientId}/environments/`,
          { environment_id: env.id }
        );
        if (resp.task_id) await pollTask(resp.task_id);
      }
      await load();
      enqueueSnackbar(t("patient.env_updated", "Environments updated."), {
        variant: "success",
        autoHideDuration: 3000,
      });
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (envId: number) => {
    setBusy(true);
    try {
      const resp = await deleteDataWithBody<DispatchResp>(
        `/patients/${patientId}/environments/${envId}/`
      );
      if (resp.task_id) await pollTask(resp.task_id);
      await load();
      enqueueSnackbar(t("patient.env_updated", "Environments updated."), {
        variant: "success",
        autoHideDuration: 3000,
      });
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  };

  const disabled = loading || busy;

  return (
    <Dialog
      open={open}
      onClose={disabled ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: athenaDarkPaperSx }}
    >
      <DialogTitle sx={athenaDarkTitleSx}>
        <WorkspacesOutlinedIcon fontSize="small" />
        {t("patient.edit_environments_title", "Patient environments")}
        <IconButton
          onClick={onClose}
          disabled={disabled}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={athenaDarkContentSx}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
              {t(
                "patient.env_current_label",
                "Environments this patient belongs to"
              )}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
              {data.current.map((env) => {
                const isPersonal = env.kind === "personal";
                const canRemove = env.controllable && isPersonal;
                const lockTitle = !isPersonal
                  ? t(
                      "patient.env_managed_tooltip",
                      "Managed by the institution/consortium sync"
                    )
                  : t(
                      "patient.env_not_yours_tooltip",
                      "You do not manage this environment"
                    );
                return (
                  <Chip
                    key={env.id}
                    label={env.name}
                    size="small"
                    onDelete={canRemove ? () => handleRemove(env.id) : undefined}
                    disabled={busy}
                    icon={
                      canRemove ? undefined : (
                        <Tooltip title={lockTitle}>
                          <LockOutlinedIcon sx={{ fontSize: 14 }} />
                        </Tooltip>
                      )
                    }
                  />
                );
              })}
              {data.current.length === 0 && (
                <Typography variant="body2">
                  {t("patient.env_none", "No environments.")}
                </Typography>
              )}
            </Box>

            <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
              {t("patient.env_add_label", "Add to personal environments")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <Autocomplete
                multiple
                fullWidth
                size="small"
                options={data.addable}
                value={selectedAdd}
                onChange={(_, val) => setSelectedAdd(val)}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                disabled={disabled || data.addable.length === 0}
                noOptionsText={t(
                  "patient.env_no_addable",
                  "No more personal environments you manage to add."
                )}
                slotProps={{
                  paper: {
                    sx: {
                      // Match the dark dialog body so the dropdown menu reads
                      // as part of the same surface. Without this the popper
                      // renders default light Paper and the option text was
                      // invisible against the user's environment names.
                      // Joan, 2026-06-04 (cursor_logs/036 §16).
                      background: `linear-gradient(160deg, ${athenaColors.deepNavy} 0%, ${alpha(athenaColors.royalBlue, 0.95)} 100%)`,
                      color: athenaColors.white,
                      border: `1px solid ${alpha(athenaColors.periwinkle, 0.3)}`,
                      "& .MuiAutocomplete-option": {
                        color: athenaColors.white,
                        "&:hover, &.Mui-focused, &[aria-selected='true']": {
                          backgroundColor: alpha(athenaColors.periwinkle, 0.18),
                        },
                        "&[aria-selected='true'].Mui-focused": {
                          backgroundColor: alpha(athenaColors.periwinkle, 0.28),
                        },
                      },
                      "& .MuiAutocomplete-noOptions": {
                        color: alpha(athenaColors.white, 0.7),
                      },
                    },
                  },
                  listbox: {
                    sx: { maxHeight: 320 },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      selectedAdd.length === 0
                        ? t(
                            "patient.env_add_placeholder",
                            "Search environments…"
                          )
                        : ""
                    }
                  />
                )}
              />
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={disabled || selectedAdd.length === 0}
                sx={{ ...athenaDarkPrimaryButtonSx, flexShrink: 0, mt: 0.25 }}
                startIcon={
                  busy ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : undefined
                }
              >
                {t("common.add", "Add")}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={athenaDarkActionsSx}>
        <Button onClick={onClose} disabled={disabled} sx={athenaDarkSecondaryButtonSx}>
          {t("common.close", "Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientEnvironmentsModal;
